/**
 * Formateadores y helpers para el informe Gestió Horària.
 */

import { format } from 'date-fns';

import { takeBase } from '../../../../components/display/tables/types';
import { OperatorRow, TimeManagementFilters } from './types';

export function formatHHMM(minutes: number | null | undefined): string {
  const m = minutes ?? 0;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * Descripción de una fila de turno de RV para los exports: nombre del RV,
 * nº de tramos y, si lo hay, lo descontado por actuaciones declaradas de la
 * persona (regla de la persona única). Las filas de actuación devuelven su
 * descripción tal cual (o `fallback`).
 */
export function shiftRowDescription(
  o: {
    sourceType?: string | null;
    description?: string | null;
    segmentsCount?: number | null;
    deductedMinutes?: number | null;
  },
  t: (key: string, opts?: Record<string, unknown>) => string,
  fallback: string,
): string {
  const base = o.description ?? fallback;
  if (o.sourceType !== 'surveillanceRoute') return base;
  const parts = [base];
  if (o.segmentsCount != null && o.segmentsCount > 0) {
    parts.push(
      t('surveillance.segmentsCountShort', { ns: 'pages', count: o.segmentsCount }),
    );
  }
  if ((o.deductedMinutes ?? 0) > 0) {
    parts.push(
      t('surveillance.shiftDeducted', {
        ns: 'pages',
        hours: formatHHMM(o.deductedMinutes),
      }),
    );
  }
  return parts.join(' · ').trim();
}

/**
 * Hora del día HH:MM de un instante ISO, con el MISMO formato que la pantalla
 * de la actuación: `ActionSummary` rinde `startHour`/`endHour` con
 * `format(new Date(registerInitDate), 'HH:mm')` (date-fns, hora local).
 * Igualar el formato evita que el tramo horario del informe difiera de la
 * actuación (origen del bug "8h en gestión vs 5:30 en la actuación").
 */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'HH:mm');
}

/**
 * Fecha + hora "dd/MM/yyyy HH:mm" de un instante ISO (— si falta/inválida).
 * Para las columnas "Fecha inicio"/"Fecha fin" del detalle: muestra el día
 * además de la hora, así un tramo cross-day (fin el día siguiente) se ve.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd/MM/yyyy HH:mm');
}

/**
 * Formato para horas ESTIMADAS (empresas externas): MISMA precisión HH:MM
 * que las reales —la estimación = duración OT × nº operarios es un cálculo
 * exacto— pero con marcador `~` delante para señalar que es una estimación,
 * no horas fichadas. La distinción real/estimado la refuerzan además el chip
 * "Estimado", la sección y el banner; NO se destruye la precisión (redondear
 * a la hora perdía información útil: 01:38 → "~2 h" engañaba más que aclaraba).
 */
export function formatEstimatedHours(
  minutes: number | null | undefined,
): string {
  return `~${formatHHMM(minutes)}`;
}

/**
 * Total agregado de minutos de una lista de filas del report.
 */
export function sumMinutes(rows: Array<{ totalMinutes: number }>): number {
  return rows.reduce((acc, r) => acc + (r.totalMinutes ?? 0), 0);
}

// Rango por defecto del report: del día 1 del mes actual hasta ahora.
// UX típica de los Informes: abrir y ver el mes en curso sin tocar nada.
// NOTA: usamos `toISOString()` (UTC). InputCalendar después parsea y
// muestra en local time, así que en TZ con offset positivo el "01/05" se
// envía como "2026-04-30T22:00:00Z" y el picker muestra el día correcto
// porque también parsea como local. No tocar sin probar otras TZ.
export function defaultDateRange(): { fromDate: string; toDate: string } {
  const now = new Date();
  const fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const toDate = now.toISOString();
  return { fromDate, toDate };
}

// Shape mínimo del cursor Redux que usa este report (subset de
// DataCursorArrowTables). El reducer guarda lo que le pasemos.
interface TimeManagementCursor {
  fromDate?: string;
  toDate?: string;
  teamIds?: string[];
  userIds?: string[];
  externalCompanyIds?: string[];
  states?: string[];
}

/**
 * Construye los `TimeManagementFilters` (estado UI) a partir del cursor
 * Redux persistido, cayendo a los defaults (mes actual) si falta.
 * Usado por el loader y por la página para tener una sola fuente de verdad.
 */
export function cursorToFilters(
  cursor: TimeManagementCursor | undefined | null,
  defaults: { fromDate: string; toDate: string },
): TimeManagementFilters {
  return {
    fromDate: cursor?.fromDate ?? defaults.fromDate,
    toDate: cursor?.toDate ?? defaults.toDate,
    teamIds: cursor?.teamIds ?? [],
    userIds: cursor?.userIds ?? [],
    externalCompanyIds: cursor?.externalCompanyIds ?? [],
    // SIN default: el filtro de Estado arranca VACÍO = todos los no-anulados.
    // Antes defaulteaba a Validada+Pendiente de certificar, pero pre-filtrar
    // confundía al usuario ("faltan actuaciones") cuando en realidad estaban en
    // otro estado (p.ej. Abierta). Ahora se ve todo por defecto y el usuario
    // selecciona Validada+Pendiente a mano cuando quiere la vista de
    // certificación. Igual que el resto de arrays: vacío/undefined = sin filtro.
    states: cursor?.states ?? [],
  };
}

/**
 * Serializa los filtros UI al objeto que se persiste en el cursor Redux
 * (`setCursor`). El reducer reemplaza (no merge) → objeto completo.
 */
export function filtersToCursor(f: TimeManagementFilters): TimeManagementCursor & {
  take: number;
  cursor: string;
  orderBy: string;
} {
  return {
    take: takeBase,
    cursor: '',
    orderBy: 'desc',
    fromDate: f.fromDate,
    toDate: f.toDate,
    teamIds: f.teamIds.length > 0 ? f.teamIds : undefined,
    userIds: f.userIds.length > 0 ? f.userIds : undefined,
    externalCompanyIds:
      f.externalCompanyIds.length > 0 ? f.externalCompanyIds : undefined,
    // Arrays vacíos → undefined (= sin filtro). `states` ya no tiene default
    // no-vacío (se quitó el de certificación), así que es consistente con el
    // resto: vacío = todos los no-anulados (el backend lo resuelve).
    states: f.states.length > 0 ? f.states : undefined,
  };
}

/**
 * Traduce los filtros UI a las variables del input GraphQL
 * `LaborHoursReportFilter` (arrays vacíos → undefined).
 */
export function toReportFilter(f: TimeManagementFilters): {
  fromDate: string;
  toDate: string;
  teamIds?: string[];
  userIds?: string[];
  externalCompanyIds?: string[];
  states?: string[];
} {
  return {
    fromDate: f.fromDate,
    toDate: f.toDate,
    teamIds: f.teamIds.length > 0 ? f.teamIds : undefined,
    userIds: f.userIds.length > 0 ? f.userIds : undefined,
    externalCompanyIds:
      f.externalCompanyIds.length > 0 ? f.externalCompanyIds : undefined,
    states: f.states.length > 0 ? f.states : undefined,
  };
}

export interface DayGroupEntry {
  roid: string;
  /** Minutos imputados A ESTE DÍA (si la OT cruzó medianoche, solo su parte). */
  minutes: number;
  isSimultaneous: boolean;
  /** Turno de Recorregut de Vigilància (`sourceType === 'surveillanceRoute'`). */
  isSurveillanceRoute: boolean;
  /**
   * La OT cruza medianoche y se ha repartido: aparece en más de un día, cada
   * uno con su parte. Sirve para marcarla en la lista y que nadie lea la parte
   * como si fuera la actuación entera.
   */
  isSplit?: boolean;
}

/**
 * Reparte el intervalo [inicio, fin) por días naturales LOCALES y devuelve,
 * por cada día tocado, cuántos milisegundos le corresponden.
 *
 * Si el fin no es posterior al inicio (dato incompleto o incoherente) devuelve
 * un único tramo en el día de inicio: nunca inventa días.
 */
function repartirPorDia(
  inicio: Date,
  fin: Date | null,
): Array<{ key: string; label: string; ms: number }> {
  const unDia = [
    { key: format(inicio, 'yyyy-MM-dd'), label: format(inicio, 'dd/MM/yyyy'), ms: 0 },
  ];
  if (!fin || !(fin.getTime() > inicio.getTime())) return unDia;

  const tramos: Array<{ key: string; label: string; ms: number }> = [];
  let cursor = new Date(inicio);
  // Tope de seguridad: una OT no debería abarcar más de un mes. Evita que un
  // dato corrupto (fin en el año 3000) haga girar el bucle sin fin.
  for (let i = 0; i < 32 && cursor.getTime() < fin.getTime(); i++) {
    const medianoche = new Date(cursor);
    medianoche.setHours(24, 0, 0, 0);
    const hasta = medianoche.getTime() < fin.getTime() ? medianoche : fin;
    tramos.push({
      key: format(cursor, 'yyyy-MM-dd'),
      label: format(cursor, 'dd/MM/yyyy'),
      ms: hasta.getTime() - cursor.getTime(),
    });
    cursor = hasta;
  }
  return tramos.length ? tramos : unDia;
}

/**
 * Reparte `minutos` entre los tramos, en proporción a su duración real.
 *
 * Se reparte el TOTAL declarado en vez de recalcularlo desde las horas porque
 * `minutes` no siempre es `fin - inicio`: en empresas externas son minutos
 * ESTIMADOS (duración × nº de operarios). Repartir en proporción conserva el
 * total del sujeto pase lo que pase — que es la propiedad que no se puede
 * romper en un informe de certificación.
 *
 * El resto del redondeo va al último tramo, así la suma de las partes es
 * EXACTAMENTE el total original (sin céntimos perdidos).
 */
function repartirMinutos(minutos: number, tramos: Array<{ ms: number }>): number[] {
  if (tramos.length <= 1) return [minutos];
  const totalMs = tramos.reduce((acc, t) => acc + t.ms, 0);
  if (totalMs <= 0) return tramos.map((_, i) => (i === 0 ? minutos : 0));
  const partes = tramos.map((t) => Math.round((minutos * t.ms) / totalMs));
  const desviacion = minutos - partes.reduce((a, b) => a + b, 0);
  partes[partes.length - 1] += desviacion;
  return partes;
}

export interface DayGroup {
  /** 'yyyy-MM-dd' local, o 'no-date' para OTs sin startTime. */
  dayKey: string;
  /** 'dd/MM/yyyy' local, o '—' para la cubeta sin fecha. */
  dayLabel: string;
  /** Minutos del día EXCLUYENDO simultáneas (espejo del backend/informe). */
  totalMinutes: number;
  /** Nº de OTs listadas ese día (las simultáneas SÍ cuentan aquí). */
  otCount: number;
  entries: DayGroupEntry[];
}

/**
 * Agrupa las OTs de un sujeto por día natural LOCAL. Única fuente de la vista
 * "por trabajador y día" de los exports (Excel hoja 4 y tabla-resumen del PDF).
 *
 * Reglas:
 *  - Simultáneas: se LISTAN pero no suman (ya cuentan en el trabajo principal).
 *  - **OT que cruza medianoche: se REPARTE entre los días que abarca**, en
 *    proporción al tiempo pasado en cada uno (decisión del cliente, 2026-08-03,
 *    para que la certificación por día no deje días a cero con gente
 *    trabajando). Antes iba entera al día en que empezaba.
 *  - Sin `startTime`: cubeta '—' al final; sin `endTime` no se puede repartir,
 *    así que va entera al día de inicio (comportamiento anterior).
 *
 * Invariante que NO se puede romper: la suma de todos los días es igual al
 * total del sujeto. El reparto distribuye, nunca crea ni destruye minutos.
 */
export function groupOrdersByDay(row: OperatorRow): DayGroup[] {
  const byKey = new Map<string, DayGroup>();
  const asegurarGrupo = (key: string, label: string): DayGroup => {
    let group = byKey.get(key);
    if (!group) {
      group = { dayKey: key, dayLabel: label, totalMinutes: 0, otCount: 0, entries: [] };
      byKey.set(key, group);
    }
    return group;
  };

  for (const o of row.orders) {
    const start = o.startTime ? new Date(o.startTime) : null;
    if (!start || Number.isNaN(start.getTime())) {
      const group = asegurarGrupo('no-date', '—');
      group.otCount += 1;
      if (!o.isSimultaneous) group.totalMinutes += o.minutes;
      group.entries.push({
        roid: o.roid,
        minutes: o.minutes,
        isSimultaneous: !!o.isSimultaneous,
        isSurveillanceRoute: o.sourceType === 'surveillanceRoute',
      });
      continue;
    }

    const end = o.endTime ? new Date(o.endTime) : null;
    const tramos = repartirPorDia(
      start,
      end && !Number.isNaN(end.getTime()) ? end : null,
    );
    const minutosPorTramo = repartirMinutos(o.minutes, tramos);
    const partida = tramos.length > 1;

    tramos.forEach((tramo, i) => {
      const group = asegurarGrupo(tramo.key, tramo.label);
      group.otCount += 1;
      if (!o.isSimultaneous) group.totalMinutes += minutosPorTramo[i];
      group.entries.push({
        roid: o.roid,
        minutes: minutosPorTramo[i],
        isSimultaneous: !!o.isSimultaneous,
        isSurveillanceRoute: o.sourceType === 'surveillanceRoute',
        ...(partida ? { isSplit: true } : {}),
      });
    });
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.dayKey === 'no-date') return 1;
    if (b.dayKey === 'no-date') return -1;
    return a.dayKey.localeCompare(b.dayKey);
  });
}

/**
 * Lista compacta "ROID (HH:MM)" de las OTs de un día, con el mismo distintivo
 * que la columna del detalle: turno de RV o simultánea (RV manda, igual que
 * en generateExcel/generatePdf). Los labels traducidos llegan por parámetro
 * (utils no conoce t()).
 */
export function dayEntriesLabel(
  entries: DayGroupEntry[],
  labels: { simultaneous: string; surveillanceRoute: string },
): string {
  return entries
    .map((e) => {
      const mark = e.isSurveillanceRoute
        ? ` · ${labels.surveillanceRoute}`
        : e.isSimultaneous
          ? ` · ${labels.simultaneous}`
          : '';
      return `${e.roid} (${formatHHMM(e.minutes)})${mark}`;
    })
    .join(' · ');
}
